import React from 'react';
// import LinearGradient from 'react-native-linear-gradient';
import { Image, Platform, Text, TouchableOpacity, View } from 'react-native';
import { Card, IconButton, Paragraph } from 'react-native-paper';

import { IHorizontalCardList } from '@nocode/types';
import { get } from 'lodash';
import {
  getActions,
  getItemListClick,
  getValueBinding,
  isCheckColor,
} from '../../shared';
import { CardDetail } from './index';
import { checkFont } from '../../func';
import { defaultURL } from '@common/constants';
import { imageUrl, getPlaceholder } from '../../../utils/handleCardDetail';
import { useImageSecurity } from '@common/components/ImageSecurity';

type Props = {
  cardItem: CardDetail;
  obj: IHorizontalCardList;
  index: number;
  heightCard: number | undefined;
  onPress?: (id: string | undefined, options: Record<string, any>) => void;
  isLast: boolean;
  cardMargin?: {
    marginBottom: number;
    marginTop: number;
    marginRight: number;
    marginLeft: number;
  };
  finalStyle?: any;
};

const CardItem = ({
  cardItem,
  obj,
  index,
  heightCard,
  onPress,
  isLast,
  cardMargin,
  finalStyle,
}: Props) => {
  const { attributes } = obj;

  const {
    subTitle,
    subTitle2,
    footer,
    cardCover,
    avatarImage,
    detail,
    tag,
    title,
  } = attributes;

  const cardCoverWidth =
    cardCover?.styles?.image?.width ?? attributes?.cardCoverStyle?.size;
  const avatarSize = avatarImage.size || 64;
  const PADDING_BODY = 32;
  const AVATAR_MARGIN_RIGHT = 20;

  const cardWrapperContentStyle =
    Platform.OS === 'ios'
      ? {
          shadowColor: finalStyle?.shadowColor || attributes.shadow?.color,
          shadowOffset: finalStyle?.shadowOffset || {
            width: attributes.shadow?.x,
            height: attributes.shadow?.y,
          },
          shadowOpacity: finalStyle?.shadowOpacity || attributes.shadow?.blur,
          shadowRadius: finalStyle?.shadowRadius || attributes.shadow?.size,
        }
      : {};

  const cardWrapperStyle = {
    width:
      cardCoverWidth +
      ((finalStyle?.borderWidth || attributes?.borderWidth) * 2 || 0),
    borderRadius: finalStyle?.borderRadius || attributes?.borderRadius,
    borderColor: finalStyle?.borderColor || attributes?.borderColor,
    borderWidth: finalStyle?.borderWidth || attributes?.borderWidth,
    borderStyle:
      (finalStyle?.borderStyle || attributes?.borderStyle) !== 'none'
        ? finalStyle?.borderStyle || attributes?.borderStyle
        : undefined,
    backgroundColor: finalStyle?.backgroundColor || attributes?.backgroundColor,
    opacity: finalStyle?.opacity || attributes?.opacity || 1,
    overflow: 'hidden' as const,
    shadowColor: finalStyle?.shadowColor || attributes.shadow?.color,
    shadowOffset: finalStyle?.shadowOffset || {
      width: attributes.shadow?.x,
      height: attributes.shadow?.y,
    },
    shadowOpacity: finalStyle?.shadowOpacity || attributes.shadow?.blur,
    shadowRadius: finalStyle?.shadowRadius || attributes.shadow?.size,
    marginBottom: cardMargin?.marginBottom,
    marginTop: cardMargin?.marginTop,
    marginLeft: index === 0 ? cardMargin?.marginLeft : 0,
    marginRight: isLast ? cardMargin?.marginRight : 0,
    elevation: finalStyle?.elevation || 5,
  };

  const cardImageStyle = {
    width: cardCover?.styles?.image?.width ?? '100%',
    ...(attributes.design === 'v1' && {
      height: cardCover?.styles?.image?.height,
    }),
    ...(attributes.design === 'v2' && {
      height: 250,
    }),
    borderRadius: cardCover?.styles?.image?.rounding,
    borderTopLeftRadius: cardCover?.styles?.image?.rounding,
    borderTopRightRadius: cardCover?.styles?.image?.rounding,
  };

  const getTextStyle = (styleConfig: any, key?: any) => {
    const styleObject = key ? styleConfig[key] : styleConfig;
    return {
      fontFamily: checkFont(styleObject?.fontFamily || finalStyle.fontFamily),
      fontSize: styleObject?.fontSize || styleConfig?.size,
      color: styleObject?.color || styleConfig?.color,
      marginVertical: 6,
      fontWeight: finalStyle?.fontWeight?.toString() || '700',
    };
  };

  const cardTitleStyle = {
    ...(title?.styles?.text?.lineHeight === 2 && { flexGrow: 1 }),
    ...getTextStyle(title?.styles?.text || {}),
  };

  const cardSubTitleStyle = {
    ...(subTitle?.subtitleLineNum === 2 && { flexGrow: 1 }),
    ...(subTitle?.subtitleLineNum === 1 &&
      attributes.design === 'v2' && {
        maxWidth: cardCoverWidth - PADDING_BODY,
      }),
    ...(attributes.design === 'v2' && { flex: 1 }),
    ...getTextStyle(subTitle?.styles?.text),
  };

  const cardSubTitle2Style = {
    ...getTextStyle(subTitle2?.styles?.text || {}),
  };

  const cardDetailStyle = {
    ...getTextStyle(detail?.styles?.text || {}),
  };

  const cardTagStyle = {
    ...getTextStyle({ ...tag?.styles?.text, isTag: true }),
  };

  const cardFooterStyle = {
    ...getTextStyle(footer?.styles),
  };

  const cardTitleWrapperStyle = {
    marginLeft: attributes.title.icon ? -10 : 0,
    paddingTop: 10,
    ...(attributes?.avatarImage?.enabled && { minHeight: avatarSize / 2 }),
    maxWidth:
      cardCoverWidth -
      (attributes?.avatarImage?.enabled
        ? avatarSize + AVATAR_MARGIN_RIGHT + PADDING_BODY / 2
        : PADDING_BODY),
  };

  const cardSubTitleWrapperStyle = {
    marginLeft: attributes.subTitle?.icon ? -10 : 0,
    ...(attributes?.subTitle?.subtitleLineNum === 1 &&
      attributes.design === 'v2' && {
        maxWidth: cardCoverWidth - PADDING_BODY,
      }),
  };

  const cardWrapperSubTitle2Style = {
    flexDirection: 'row' as const,
    justifyContent: 'flex-start' as const,
    alignItems: 'center' as const,
    borderRadius: 0,
  };

  const cardWrapperTagStyle = {
    ...(attributes.design === 'v2'
      ? {
          minHeight: 37,
          position: 'absolute' as const,
          left: 0,
          top: -50,
          paddingRight: Platform.OS === 'web' ? 20 : 50,
          paddingLeft: attributes.tag?.icon ? 0 : 8,
          paddingVertical: 4,
        }
      : {
          paddingHorizontal: 8,
          paddingLeft: attributes.tag?.icon ? 0 : 8,
          paddingVertical: 4,
        }),
    backgroundColor:
      tag?.styles?.text?.backgroundColor ??
      attributes.tagStyle?.backgroundColor,
    borderBottomEndRadius: attributes.tagStyle?.borderRadius,
    borderTopEndRadius: attributes.tagStyle?.borderRadius,
    maxWidth:
      cardCoverWidth -
      (attributes.design === 'v2'
        ? avatarSize + AVATAR_MARGIN_RIGHT
        : PADDING_BODY),
    display: 'flex' as const,
    flexDirection: 'row' as const,
    justifyContent: 'flex-start' as const,
    alignItems: 'center' as const,
  };

  const cardFooterWrapperStyle = {
    borderTopWidth: 1,
    borderColor: `rgba(${attributes?.footerStyle?.borderTopColor}, ${attributes?.footerStyle?.borderTopOpacity})`,
    backgroundColor: attributes?.footerStyle?.backgroundColor,
    display: 'flex' as const,
    paddingHorizontal:
      (finalStyle?.borderRadius || attributes?.borderRadius) / 2,
    ...(attributes?.footer?.footerLineNum === 1 && {
      maxWidth: cardCoverWidth,
    }),
    paddingRight: avatarSize / 4,
  };

  const avatarWrapperStyle = {
    position: 'absolute' as const,
    right: AVATAR_MARGIN_RIGHT,
    top: -(avatarSize / 2),
    backgroundColor: '#eeeeee',
    borderStyle: (finalStyle?.borderStyle ||
      attributes.avatarStyle?.borderStyle ||
      'solid') as any,
    borderWidth:
      finalStyle?.borderWidth || attributes.avatarStyle?.borderWidth || 1,
    borderColor:
      finalStyle?.borderColor || attributes.avatarStyle?.borderColor || 'white',
    height: avatarSize,
    width: avatarSize,
    borderRadius:
      finalStyle?.borderRadius ||
      attributes.avatarStyle?.borderRadius ||
      avatarSize,
  };

  const avatarStyle = {
    height: '100%' as const,
    width: '100%' as const,
    borderRadius:
      finalStyle?.borderRadius ||
      attributes.avatarStyle?.borderRadius ||
      avatarSize,
  };

  const contentWrapperStyle = {
    display: 'flex' as const,
    flexDirection: 'row' as const,
    justifyContent: 'flex-start' as const,
    alignItems: 'center' as const,
    borderRadius: 0,
    gap: 4,
  };

  const emptyWrapperStyle = {
    height: avatarSize / 2,
  };

  const cardContentWrapperStyle = {
    flex: 1,
    ...(attributes.design === 'v1' && {
      zIndex: 200,
      bottom: 0,
      position: 'absolute' as const,
      width: '100%' as const,
      paddingBottom: 16,
    }),
    ...(attributes.design === 'v2' && {
      backgroundColor:
        finalStyle?.backgroundColor || attributes.backgroundColor,
    }),
    ...(attributes?.avatarImage?.enabled && {
      minHeight: avatarSize / 2 + 1,
    }),
  };

  const cardCoverUri = React.useMemo(
    () => imageUrl(attributes?.cardCover, cardItem),
    [attributes?.cardCover, cardItem]
  );
  const avatarUri = React.useMemo(
    () => imageUrl(attributes?.avatarImage, cardItem, 'avtImge'),
    [attributes?.avatarImage, cardItem]
  );

  const designVersion = attributes.design || 'v2';

  const [cardUrl, setCardUrl] = React.useState(cardCoverUri);
  const [avatarUrl, setAvatarUrl] = React.useState(avatarUri);

  React.useLayoutEffect(() => {
    setCardUrl(cardCoverUri);
  }, [cardCoverUri]);
  React.useLayoutEffect(() => {
    setAvatarUrl(avatarUri);
  }, [avatarUri]);

  const { imageUrl: secureCardUrl } = useImageSecurity(cardUrl);
  const { imageUrl: secureAvatarUrl } = useImageSecurity(avatarUrl);
  const { imageUrl: secureCardPlaceHolderUrl } = useImageSecurity(
    getPlaceholder(attributes?.cardCover)
  );
  const { imageUrl: secureAvatarPlaceholder } = useImageSecurity(
    getPlaceholder(attributes?.avatarImage)
  );

  const handlePress = (id: string, item: Record<string, any>) => {
    const options = {
      itemListClick: getItemListClick(item.record),
    };

    onPress && onPress(getActions(obj, id), options);
  };

  const isElementEmpty =
    !attributes.title.enabled && attributes.avatarImage.enabled;

  const isCanvas =
    window?.location?.href &&
    window.location.href.split('/').includes('canvas');

  const bindingValue = getValueBinding('', cardItem, obj);

  return (
    <View style={cardWrapperContentStyle}>
      <Card
        style={[
          cardWrapperStyle,
          {
            ...(heightCard &&
              !isCanvas && {
                height:
                  heightCard -
                  (cardMargin?.marginTop ?? 0) -
                  (cardMargin?.marginBottom ?? 0),
              }),
          },
        ]}
        onPress={() => {
          handlePress('onPress', {
            record: cardItem,
            indexRecord: index,
          });
        }}
      >
        {Platform.OS === 'web' ? (
          <img
            src={
              secureCardUrl
                ? secureCardUrl
                : secureCardPlaceHolderUrl || defaultURL
            }
            alt=""
            style={{
              objectFit: attributes?.cardCover?.image?.imageResize || 'cover',
              ...cardImageStyle,
            }}
            onError={() => {
              setCardUrl(secureCardPlaceHolderUrl || defaultURL);
            }}
          />
        ) : (
          <Image
            style={cardImageStyle}
            source={{
              uri: secureCardUrl
                ? secureCardUrl
                : secureCardPlaceHolderUrl || defaultURL,
            }}
            onError={() => {
              setCardUrl(secureCardPlaceHolderUrl || defaultURL);
            }}
            resizeMode={attributes?.cardCover?.image?.imageResize || 'cover'}
          />
        )}

        <Card.Content style={cardContentWrapperStyle}>
          {designVersion === 'v1' ? (
            <View style={contentWrapperStyle}>
              {attributes?.tag?.enabled && (
                <TouchableOpacity style={cardWrapperTagStyle}>
                  {!!attributes.tag.icon && (
                    <IconButton
                      size={12}
                      icon={attributes.tag.icon}
                      color={
                        isCheckColor(tag?.styles?.text?.iconColor)
                          ? tag?.styles?.text?.iconColor
                          : '#000000'
                      }
                      style={{ borderRadius: 2 }}
                    />
                  )}

                  <Paragraph
                    style={cardTagStyle}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {get(bindingValue, 'tag.text')}
                  </Paragraph>
                </TouchableOpacity>
              )}
              {attributes?.detail?.enabled && (
                <Text style={cardDetailStyle}>
                  {get(bindingValue, 'detail.text')}
                </Text>
              )}
            </View>
          ) : null}
          {attributes?.title?.enabled && (
            <View style={[contentWrapperStyle, cardTitleWrapperStyle]}>
              {!!attributes.title.icon && (
                <IconButton
                  size={24}
                  icon={attributes.title.icon}
                  color={attributes.titleStyle.iconColor}
                />
              )}
              <Text
                style={cardTitleStyle}
                ellipsizeMode="tail"
                numberOfLines={attributes?.title?.titleLineNum === 1 ? 1 : 2}
              >
                {get(bindingValue, 'title.text')}
              </Text>
            </View>
          )}
          {isElementEmpty && <View style={emptyWrapperStyle} />}

          {attributes?.subTitle?.enabled && designVersion === 'v2' && (
            <View style={[contentWrapperStyle, cardSubTitleWrapperStyle]}>
              {!!attributes.subTitle.icon && (
                <IconButton
                  size={12}
                  icon={attributes.subTitle.icon}
                  color={attributes.subTitleStyle.iconColor}
                />
              )}
              <Text
                style={cardSubTitleStyle}
                ellipsizeMode="tail"
                numberOfLines={
                  attributes?.subTitle?.subtitleLineNum === 1 ? 1 : 2
                }
              >
                {get(bindingValue, 'subTitle.text')}
              </Text>
            </View>
          )}

          {attributes.subTitle2?.enabled && designVersion === 'v2' && (
            <TouchableOpacity style={cardWrapperSubTitle2Style}>
              {!!attributes.subTitle2.icon && (
                <IconButton
                  size={12}
                  icon={attributes.subTitle2.icon}
                  color={
                    isCheckColor(attributes.subTitle2Style.iconColor)
                      ? attributes.subTitle2Style.iconColor
                      : '#000000'
                  }
                />
              )}

              <Text
                style={cardSubTitle2Style}
                ellipsizeMode="tail"
                numberOfLines={1}
              >
                {get(bindingValue, 'subTitle2.text')}
              </Text>
            </TouchableOpacity>
          )}

          {attributes?.avatarImage?.enabled && designVersion === 'v2' && (
            <View style={avatarWrapperStyle}>
              <Image
                style={avatarStyle}
                source={{
                  uri: secureAvatarUrl
                    ? secureAvatarUrl
                    : secureAvatarPlaceholder || defaultURL,
                }}
                onError={() => {
                  setAvatarUrl(secureAvatarPlaceholder || defaultURL);
                }}
                resizeMode={avatarImage.image?.imageResize || 'cover'}
              />
            </View>
          )}

          {attributes?.tag?.enabled && designVersion === 'v2' && (
            <TouchableOpacity style={cardWrapperTagStyle}>
              {!!attributes.tag.icon && (
                <IconButton
                  size={12}
                  icon={attributes.tag.icon}
                  color={
                    isCheckColor(attributes.tagStyle.iconColor)
                      ? attributes.tagStyle.iconColor
                      : '#000000'
                  }
                />
              )}

              <Paragraph
                style={cardTagStyle}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {get(bindingValue, 'tag.text')}
              </Paragraph>
            </TouchableOpacity>
          )}

          {designVersion === 'v1' ? (
            <View style={contentWrapperStyle}>
              {attributes?.subTitle?.enabled && (
                <Text
                  style={cardSubTitleStyle}
                  ellipsizeMode="tail"
                  numberOfLines={
                    attributes?.subTitle?.subtitleLineNum === 1 ? 1 : 2
                  }
                >
                  {get(bindingValue, 'subTitle.text')}
                </Text>
              )}

              {attributes?.subTitle2?.enabled && (
                <Text
                  style={cardSubTitle2Style}
                  ellipsizeMode="tail"
                  numberOfLines={1}
                >
                  | {get(bindingValue, 'subTitle2.text')}
                </Text>
              )}
            </View>
          ) : null}
        </Card.Content>

        {attributes?.footer?.enabled && designVersion === 'v2' && (
          <Card.Actions style={cardFooterWrapperStyle}>
            {!!attributes.footer.icon && (
              <IconButton
                style={{ marginLeft: 0 }}
                size={12}
                icon={attributes.footer.icon}
                color={attributes.footerStyle.iconColor}
              />
            )}
            <Text
              style={cardFooterStyle}
              ellipsizeMode="tail"
              numberOfLines={attributes?.footer?.footerLineNum}
            >
              {get(bindingValue, 'footer.text')}
            </Text>
          </Card.Actions>
        )}
      </Card>
    </View>
  );
};

export default CardItem;
